import  {useEffect, useMemo, useState} from 'react'
import language from '../../variables/language';
import '../../css/form.css'
import '../../css/clients-list.css'
import Selector from '../../components/forms/Selector';
import TagsEditor from '../../components/forms/TagsEditor';
import DateInput from '../../components/forms/DateInput';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ChatComponent from '../../components/websocket/ChatComponent';
import IconChat from '../../components/icons/IconChat';
import ConvProgressPart from './ConvProgressPart';
import IconGoBack from '../../components/icons/IconGoBack';
import IconAdd from '../../components/icons/IconAdd';
import IconDone from '../../components/icons/IconDone';
import IconSudent from '../../components/icons/IconSudent';
import IconSave from '../../components/icons/IconSave';
import ConversationsMenu from './ConversationsMenu';
import { DateTime } from 'luxon';

// Clients with no contact for longer than this are highlighted on the card.
const STALE_CONTACT_DAYS = 7;

// dLastContact is a plain string. Empty, missing and the literal "null" (left
// behind by old imports) all mean "never contacted".
function parseLastContact(value) {
   if (!value || value === 'null') return null;
   const dt = DateTime.fromISO(String(value));
   return dt.isValid ? dt : null;
}

function daysAgoText(days) {
   if (days <= 0) return 'сьогодні';
   if (days === 1) return 'вчора';
   return days + ' дн. тому';
}

const TEXT_FIELDS = [
   {name:'firstName', type:'text'},
   {name:'lastName', type:'text'},
   {name:'email', type:'text'},
   {name:'tel', type:'number'},
];
const SALES_FIELDS = [
   {name:'price', type:'number'},
   {name:'probability', type:'number'},
   {name:'dLastContact', type:'date'},
];
const SALES_SELECTORS = [
   {name:'responsibleId', url:'/api/users'},
   {name:'source', url:'/api/sources'},
   {name:'product', url:'/api/products'},
   {name:'category', url:'/api/categories'},
   {name:'project', url:'/api/projects'},
];

const resolveValue = (val) => {
   if(val && typeof val === 'object' && val.id !== undefined) {
      return String(val.id);
   }
   return val === null || val === undefined ? '' : String(val);
}

const flattenClientData = (data) => {
   if(!data || !data.id) {
      return null;
   }
   const flat = {};
   TEXT_FIELDS.forEach(f => flat[f.name] = resolveValue(data[f.name]));
   SALES_FIELDS.forEach(f => flat[f.name] = resolveValue(data[f.name]));
   SALES_SELECTORS.forEach(f => flat[f.name] = resolveValue(data[f.name]));
   return flat;
}

export default function FormClient(props) {
   const navigate = useNavigate();
   const [formValues, setFormValues] = useState(null);
   const [initialValues, setInitialValues] = useState(null);
   const [saving, setSaving] = useState(false);
   const [stages, setStages] = useState([]);
   const [chat , setChat ] = useState(false);
   const [conversationModal , setConversationModal ] = useState(false);
   const [tagNames, setTagNames] = useState([]);
   const [linkedStudent, setLinkedStudent] = useState(null);
   const [creatingStudent, setCreatingStudent] = useState(false);

   useEffect(() => {
      setTagNames((props.data.tags || []).map(t => t.name));
   }, [props.data.tags]);

   // The student this client became, shown on the card with a link to it.
   useEffect(() => {
      const studentId = props.data.studentId;
      if (!studentId) {
         setLinkedStudent(null);
         return;
      }
      axios.get('/api/students/' + studentId, { withCredentials: true })
         .then(response => setLinkedStudent(response.data))
         .catch(() => setLinkedStudent({ id: studentId, name: '', surname: '' }));
   }, [props.data.studentId]);

   useEffect(() => {
      const flat = flattenClientData(props.data);
      if(!flat) {
         return;
      }
      setFormValues(flat);
      setInitialValues(flat);
   }, [props.data]);

   useEffect(() => {
      if(!props.data || !props.data.stage || !props.data.stage.stagetype) {
         setStages([]);
         return;
      }
      axios.get('/api/stages?stageType='+props.data.stage.stagetype.id, { withCredentials: true })
         .then(response => {
            setStages(Array.isArray(response.data) ? response.data : []);
         })
         .catch(err => console.log(err));
   }, [props.data.stage && props.data.stage.stagetype && props.data.stage.stagetype.id]);

   const isDirty = useMemo(() => {
      if(!formValues || !initialValues) {
         return false;
      }
      return JSON.stringify(formValues) !== JSON.stringify(initialValues);
   }, [formValues, initialValues]);

   const handleFieldChange = (name, value) => {
      setFormValues(prev => ({...prev, [name]: value}));
   }

   const handleDiscard = () => {
      setFormValues(initialValues);
   }

   const handleSave = () => {
      if(!isDirty || saving) {
         return;
      }
      setSaving(true);
      // backend does Long.parseLong on unset FK/number fields whenever the key is present
      // at all (not just non-null), so an empty string 500s as a 401 - omit unset fields.
      const payload = {};
      Object.keys(formValues).forEach(key => {
         if(formValues[key] !== '') {
            payload[key] = formValues[key];
         }
      });
      axios.put(props.url, JSON.stringify(payload), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         props.onRefreshHundler();
      }).catch(err => {
         if(err.response && err.response.status === 401){
            navigate('/login');
         }
         console.log(err);
      }).finally(() => {
         setSaving(false);
      });
   }

   const handleQuickStageChange = (e, stageId) => {
      e.stopPropagation();
      if(props.data.stage && String(props.data.stage.id) === String(stageId)) {
         return;
      }
      axios.put(props.url, JSON.stringify({stage: stageId}), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         props.onRefreshHundler();
      }).catch(err => console.log(err));
   }

   const handleTagsChange = (names) => {
      setTagNames(names);
      axios.put(props.url + '/tags', JSON.stringify(names), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         props.onRefreshHundler();
      }).catch(err => console.log(err));
   }

   const chatTriger = () => setChat(prev => !prev);
   const conversationAddTriger = () => setConversationModal(prev => !prev);

   if(!formValues || !initialValues) {
      return <div className='client-card-page'></div>;
   }

   const stageColor = (props.data.stage && props.data.stage.color) || 'var(--lighttext-color)';
   const initials = (formValues.firstName.charAt(0) + formValues.lastName.charAt(0)).toUpperCase() || '?';

   // Writes only dLastContact: the update endpoint sets just the keys it receives,
   // so this can't overwrite the rest of the client.
   const lastContact = parseLastContact(props.data.dLastContact);
   const lastContactDays = lastContact ? Math.floor(DateTime.now().diff(lastContact, 'days').days) : null;
   const contactIsStale = lastContactDays === null || lastContactDays > STALE_CONTACT_DAYS;
   // Creates a student from this client (name, surname, e-mail) and links the two.
   // Two requests: the student first, then the client's studentId. If the second
   // fails, the student already exists, so the message says so.
   const createStudentFromClient = () => {
      if (creatingStudent) return;
      const firstName = (props.data.firstName || '').trim();
      const lastName = (props.data.lastName || '').trim();
      if (!firstName && !lastName) {
         window.alert('Клієнт без імені і прізвища - спочатку заповніть дані клієнта.');
         return;
      }
      if (JSON.stringify(formValues) !== JSON.stringify(initialValues)) {
         window.alert('Спочатку зберіть зміни в картці клієнта, потім створіть студента.');
         return;
      }
      const copied = 'Ім\'я, прізвище і e-mail. Телефон у студента не зберігається, тому лишиться тільки в клієнті.';
      if (!window.confirm('Створити студента з цього клієнта?\nСкопіюється: ' + copied)) return;
      setCreatingStudent(true);
      const jsonHeaders = { withCredentials: true, headers: { 'Content-Type': 'application/json' } };
      axios.post('/api/students', JSON.stringify({
         name: firstName,
         surname: lastName,
         email: (props.data.email || '').trim(),
         isActive: 1,
         hasBroadcast: 0,
      }), jsonHeaders)
         .then(response => {
            const studentId = response.data.id;
            return axios.put('/api/clients/' + props.data.id, JSON.stringify({ studentId: String(studentId) }), jsonHeaders)
               .catch(() => {
                  window.alert('Студента створено (id ' + studentId + '), але не вдалося пов\'язати його з клієнтом. Спробуйте ще раз.');
                  throw new Error('link failed');
               });
         })
         .then(() => {
            props.onRefreshHundler();
         })
         .catch(err => {
            if (err.message !== 'link failed') window.alert('Не вдалося створити студента.');
            console.log(err);
         })
         .finally(() => setCreatingStudent(false));
   };

   const markContactNow = () => {
      axios.put('/api/clients/' + props.data.id, JSON.stringify({ dLastContact: DateTime.now().toISO() }), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         props.onRefreshHundler();
      }).catch(err => console.log(err));
   };

  return (
    <div style={{width:'100%'}}>
      {chat ? (
         <ChatComponent clientId={props.data.id} closeCallback={() => setChat(false)} />
      ) : ''}

      <ConversationsMenu closeCallback={setConversationModal} clientId={props.data.id} openTriger={conversationModal} />

      <div className='client-card-header' style={{'--avatar-color':stageColor}}>
         <div className='client-card-back' onClick={() => navigate('/clients')} title={language[language.lang].client}>
            <IconGoBack style={{fill:'var(--lighttext-color)', width:18, height:18, margin:0}} />
         </div>
         <div className='client-card-avatar'>{initials}</div>
         <div className='client-card-title-block'>
            <h2 className='client-card-name'>{formValues.firstName} {formValues.lastName}</h2>
            {props.data.stage ? <span className='client-card-stage-badge'>{props.data.stage.name}</span> : ''}
            <div className='client-card-meta-row'>
               <span className={'client-card-meta-pill' + (contactIsStale ? ' is-warn' : '')}
                  title={lastContact ? 'Останній контакт' : 'Контакту ще не було'}>
                  <span className='client-card-meta-label'>Контакт</span>
                  {lastContact
                     ? lastContact.toFormat('dd.MM.yy') + ' · ' + daysAgoText(lastContactDays)
                     : 'ще не було'}
               </span>
               <button type='button' className='client-card-meta-action' onClick={markContactNow} title='Зберегти, що контакт був зараз'>
                  <IconDone style={{ width: 11, height: 11, margin: 0 }} />
                  Був контакт
               </button>
               {props.data.studentId ? (
                  <button type='button' className='client-card-meta-pill client-card-meta-student'
                     title='Відкрити картку студента' onClick={() => navigate('/students/' + props.data.studentId)}>
                     <IconSudent style={{ width: 12, height: 12, margin: 0 }} />
                     {linkedStudent ? ((linkedStudent.name || '') + ' ' + (linkedStudent.surname || '')).trim() || 'Студент' : '…'}
                  </button>
               ) : (
                  <button type='button' className='client-card-meta-action client-card-meta-action-dashed'
                     onClick={createStudentFromClient} disabled={creatingStudent}
                     title='Створити студента з цього клієнта'>
                     <IconAdd style={{ width: 11, height: 11, margin: 0 }} />
                     {creatingStudent ? 'Створення…' : 'Студент'}
                  </button>
               )}
            </div>
            {Array.isArray(stages) && stages.length > 0 ? (
               <div className='client-card-stage-row'>
                  {stages.map(s => (
                     <div key={s.id}
                        className={'client-card-stage-seg' + (props.data.stage && String(props.data.stage.id) === String(s.id) ? ' client-card-stage-seg-active' : '')}
                        style={{background:s.color}}
                        title={s.name}
                        onClick={(e) => handleQuickStageChange(e, s.id)}></div>
                  ))}
               </div>
            ) : ''}
         </div>
         <div className='client-card-price'>{formValues.price || 0} zł</div>
         {props.data.addedOuterUser ? (
            <div onClick={chatTriger} className='chat-icon-client' title={language[language.lang].messages}>
               <IconChat fillColor='icon-green'></IconChat>
            </div>
         ) : ''}
         {props.data.addedOuterUser ? (
            <div onClick={conversationAddTriger} className='chat-icon-client' title={language[language.lang].find}>
               <IconAdd />
            </div>
         ) : ''}
      </div>

      <div className='client-card-body'>
         <div className='client-card-form-col'>
            <form className='client-card-section' onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
               <p className='client-card-section-title'>{language[language.lang].client}</p>
               <div className='client-card-fields-grid'>
                  {TEXT_FIELDS.map(f => (
                     <div className='flex-from-input-wraper client-card-field' data-label={language[language.lang][f.name]} key={f.name}>
                        <input className='submit-field from-input txt-c' type={f.type}
                           placeholder={language[language.lang][f.name]}
                           value={formValues[f.name]}
                           onChange={(e) => handleFieldChange(f.name, e.target.value)} />
                     </div>
                  ))}
               </div>
            </form>

            <div className='client-card-section'>
               <p className='client-card-section-title'>{language[language.lang].price}</p>
               <div className='client-card-fields-grid'>
                  {SALES_FIELDS.map(f => (
                     <div className='flex-from-input-wraper client-card-field' data-label={language[language.lang][f.name]} key={f.name}>
                        {f.type === 'date' ?
                           <DateInput value={formValues[f.name]} onChange={(value) => handleFieldChange(f.name, value)} />
                        :
                           <input className='submit-field from-input txt-c' type={f.type}
                              placeholder={language[language.lang][f.name]}
                              value={formValues[f.name]}
                              onChange={(e) => handleFieldChange(f.name, e.target.value)} />
                        }
                     </div>
                  ))}
                  {SALES_SELECTORS.map(f => (
                     <div className='flex-from-input-wraper client-card-field' data-label={language[language.lang][f.name]} key={f.name}>
                        <Selector className='submit-field form-selection txt-c'
                           name={f.name} url={f.url} value={formValues[f.name]} disabled={false}
                           selectorsHundler={(value) => handleFieldChange(f.name, value)} />
                     </div>
                  ))}
               </div>
            </div>

            <div className='client-card-section'>
               <p className='client-card-section-title'>{language[language.lang].tags}</p>
               <TagsEditor tags={tagNames} onChange={handleTagsChange} />
            </div>

            {isDirty ? (
               <div className='client-card-form-footer'>
                  <span className='client-card-unsaved-hint'>{language[language.lang].unsavedChanges}</span>
                  <div className='client-card-discard-link' onClick={handleDiscard}>{language[language.lang].cancel}</div>
                  <button type='button' className='client-card-save-btn' disabled={saving} onClick={handleSave}>
                     <IconSave />
                     {saving ? language[language.lang]['Loading ...'] : language[language.lang].save}
                  </button>
               </div>
            ) : ''}
         </div>

         <div className='client-card-conv-col'>
            <ConvProgressPart className='client-card-conv-panel' clientId={props.clientId} client={props.data} />
         </div>
      </div>
    </div>
  )
}
